import Images from '../Image/images';
import { Category, ICategoryModel } from './CategoryModel';
import { GET_CATEGORIES } from '../../queries/Category';
import { IRootStore } from '../Root/RootStore';
import { Instance, flow, getParent, getRoot, types } from 'mobx-state-tree';
import { captureException } from '../../../utils/sentry/sentry';
import { clientFunction } from '../../../config/apollo/client';
import { filterDefinedFields } from '../../../utils/filterFields';
import { isUndefined } from 'lodash';

// Last resort only: used when the remote locale has not been loaded yet. Matches
// the `labels.X || 'English default'` fallbacks used elsewhere in the app.
const FACE_SCAN_FALLBACK_TITLE = 'Face scan';

// `require()` returns a numeric asset id on native, but on WEB it returns an object
// ({uri, width, height, toString}). CategoryItem.image is typed `string | number`, so the
// web object failed validation -> the item failed -> MST rejected the whole category and
// the "Health check" category vanished from the web app entirely ("No matching type for
// union"). The title was never the only problem on web. Normalise to the plain uri string;
// the card renderer already maps a string to {uri} (GradientWithImage.tsx:49), and native
// keeps the numeric id it already used.
const faceScanCardAsset: any = Images.faceScan.card;
const FACE_SCAN_CARD_IMAGE: string | number | null =
  (typeof faceScanCardAsset === 'object' && faceScanCardAsset !== null)
    ? (faceScanCardAsset.uri ?? null)
    : faceScanCardAsset;

export const CategoryStore = types
  .model('CategoryStore', {
    categories: types.array(types.maybeNull(Category)),
    state: types.maybe(types.enumeration(['pending', 'done', 'error']))
  })
  .actions(self => {
    const loadCategory = (cat: any) => {
      // Drop unusable items instead of letting one poison the whole category.
      // `categories` is an array of `maybeNull(Category)`, so a single bad item
      // failed the union and took the ENTIRE category down with it -- the user
      // lost a whole Library/dashboard section because one record was untranslated.
      const categoryItems = (cat.categoryItem ?? [])
        .map((item: any) => loadItem(item, cat))
        .filter(Boolean)
        .sort((c1: any, c2: any) => c1.order > c2.order ? 1 : -1);
      const category = {
        ...filterDefinedFields(cat,Category.properties),
        categoryItem: categoryItems
      };
      const index = self.categories.findIndex(f => (f && f._id === cat._id));
      if (index !== -1) {
        self.categories[index] = category;
      } else {
        self.categories.push(category);
      }

    };

    // Returns null when the item cannot be represented, rather than handing MST a
    // half-built object. The API is looser than this model on every field we need:
    // contentType is `nullable: true`, the exercise/content/activity/... refs are
    // all `required: false` (so a deleted target comes back null), and title is
    // `@Translatable()` -- null whenever that record has no copy in this language.
    // CategoryItem declares contentType/contentId/title as required types.string.
    const loadItem = (item: any, category: any) => {
      const catItem: any = {
        _id: item?._id,
        contentType: item?.contentType,
        order: item?.order,
      };

      switch (item?.contentType) {
      case 'CONTENT_TEXT':
      case 'CONTENT_AUDIO':
      case 'CONTENT_VIDEO':
        catItem.contentId = item.content?._id;
        catItem.title = item.content?.title;
        catItem.designSystemColor = item.content?.designSystemColor;
        catItem.image = item.content?.image;
        catItem.activityType = item.content?.activityType;
        catItem.humanReadableId = item.content?.humanReadableId;
        catItem.sessionId = item.content?.session?._id;
        break;
      case 'BREATHING':
        catItem.contentId = item.technique?._id;
        catItem.title = item.technique?.name;
        catItem.designSystemColor = item.technique?.color;
        catItem.image = item.technique?.image;
        catItem.humanReadableId = item.technique?.humanReadableId;
        break;
      case 'PATH':
        catItem.contentId = item.path?._id;
        catItem.title = item.path?.title;
        catItem.image = item.path?.image;
        catItem.designSystemColor = item.path?.designSystemColor;
        catItem.humanReadableId = item.path?.humanReadableId;
        if (item.path) { getParent<IRootStore>(self).path.loadPath(item.path, false); }
        break;
      case 'SURVEY':
        catItem.contentId = item.survey?._id;
        catItem.title = item.survey?.title;
        catItem.image = item.survey?.image;
        catItem.humanReadableId = item.survey?.humanReadableId;
        catItem.designSystemColor = item.survey?.designSystemColor;
        catItem.sessionId = item.survey?.session?._id;
        break;
      case 'EXERCISE':
        catItem.contentId = item.exercise?._id;
        catItem.title = item.exercise?.title;
        catItem.image = item.exercise?.image;
        catItem.humanReadableId = item.exercise?.humanReadableId;
        catItem.designSystemColor = item.exercise?.designSystemColor;
        if (item.exercise) { (getParent(self) as any).exercise.loadExercise({ ...item.exercise }); }
        break;
      case 'ACTIVITY':
        catItem.contentId = item.activity._id;
        catItem.title = item.activity.title;
        catItem.image = item.activity?.image;
        catItem.designSystemColor = item.activity?.designSystemColor;
        catItem.humanReadableId = item.activity?.humanReadableId;
        catItem.sessionId = item.activity?.session?._id;
        (getParent(self) as any).activity.loadActivity({ ...item.activity }, null, false);
        break;
      case 'CATEGORY':
        catItem.contentId = item.categoryTarget?._id;
        catItem.title = item.categoryTarget?.title;
        break;
      case 'FACE_SCAN':
        catItem.contentId = item._id;
        // Resolved on every call, and never empty. Two things used to make this
        // title '' -- which failed validation below and made the Face scan card
        // vanish from the Library and the dashboard:
        //   - the root was captured once when the store was instantiated, before
        //     the language store could answer;
        //   - the locale is fetched separately from the categories and can land
        //     after them (setLanguage() kicks off afterLoad() before it awaits
        //     fetchLanguage()), while the bundled fallback locale has no
        //     MOBILE.LIBRARY section at all.
        // A card in the wrong language beats a card the user cannot find.
        catItem.title = getRoot<IRootStore>(self)?.language?.translate?.MOBILE?.LIBRARY?.FACE_SCAN
          || FACE_SCAN_FALLBACK_TITLE;
        catItem.image = FACE_SCAN_CARD_IMAGE;
        catItem.humanReadableId = category?.humanReadableId;
        break;
      }

      // Name the offending record. The raw MST failure prints the whole union
      // shape and not one identifying value, which makes a report like "a
      // category is missing on demo" impossible to trace back to a row.
      const missing = ['_id', 'contentType', 'contentId', 'title']
        .filter(k => typeof catItem[k] !== 'string' || catItem[k] === '');
      if (missing.length > 0 || typeof catItem.order !== 'number') {
        captureException(new Error(
          '[category] dropped unusable categoryItem: ' + JSON.stringify({
            categoryItemId: item?._id,
            categoryId: category?._id,
            categoryHumanReadableId: category?.humanReadableId,
            contentType: item?.contentType,
            missingFields: missing,
            order: item?.order,
          })
        ));
        return null;
      }
      return catItem;
    };

    // Removes the categories the latest response no longer contains, so a
    // language switch or a deletion still takes effect.
    const pruneCategoriesExcept = (keptIds: string[]) => {
      for (let index = self.categories.length - 1; index >= 0; index--) {
        const category = self.categories[index];
        if (!category || !keptIds.includes(category._id)) {
          self.categories.splice(index, 1);
        }
      }
    };

    const fetchCategories = flow(function* (language: string) {
      self.state = 'pending';
      let res;
      try {
        res = yield clientFunction(language).query({
          query: GET_CATEGORIES,
          fetchPolicy: 'no-cache'
        });
        if (res) {
          // Per-category isolation. This forEach used to sit bare inside the outer
          // try, so the FIRST category that failed validation aborted the loop and
          // set state = 'error' -- every remaining category was never loaded and the
          // user got an empty Library, not one missing section. Now a bad category
          // costs only itself and is reported with its id.
          const loadedIds: string[] = [];
          res.data.retrieveWithAtLeastOneItem.forEach((item: any) => {
            try {
              loadCategory(item);
              loadedIds.push(item?._id);
            } catch (err) {
              captureException(new Error(
                '[category] dropped unusable category: ' + JSON.stringify({
                  categoryId: item?._id,
                  humanReadableId: item?.humanReadableId,
                  type: item?.type,
                  order: item?.order,
                  itemCount: item?.categoryItem?.length,
                  cause: err instanceof Error ? err.message : String(err),
                })
              ));
            }
          });
          pruneCategoriesExcept(loadedIds);
        }
        self.state = 'done';
      } catch (error) {
        // Degrade to stale content instead of to an error screen. The list used
        // to be emptied before the request went out, so any failed refresh -- an
        // expired token, a flaky network, an API deploy in flight -- threw away
        // categories that were still perfectly displayable and left both the
        // Library and the dashboard showing nothing but the error state.
        self.state = self.categories.length > 0 ? 'done' : 'error';
        captureException(error);

      }

      return self.categories;
    });
    const clear = () => {
      self.categories = [];
    };
    return {
      fetchCategories,
      clear
    };
  })
  .views(self => ({
    filterAccessibleItems(items: any) {
      if (!items) return items;
      const company = getRoot<IRootStore>(self)?.user?.company;
      if (company?.hasAccessToFaceScan !== false) return items;
      return items.filter((it: any) => it?.contentType !== 'FACE_SCAN');
    },
    get getCategories() {
      return self.categories;
    },
    get recommended() {
      const recommended = (getParent(self) as any)?.path.getRecommended();
      return !!recommended && recommended.map((p: any, index: number) => {
        return ({
          contentType: 'PATH',
          image: p.image,
          order: index,
          contentId: p._id,
          title: p.title,
        });
      });
    },
    get ongoing() {
      return getRoot(self).path?.getOngoing().map((p: any, index: number) => {
        return ({
          contentType: 'PATH',
          image: p.image,
          order: index,
          contentId: p._id,
          title: p.title,
        });
      }).sort((p1: any, p2: any) => {

        const isP1Active = (getParent(self) as any).path.getIsActive(p1.contentId);
        if(isP1Active) {return -1;}
        else { return 1;}
      });
    },
    getSpecial(type: string) {
      if (type === 'RECOMMENDED_PATHS') {
        return this.recommended;
      }
      if (type === 'ONGOING_PATHS') {
        return this.ongoing;
      }
    },
    getCategory(categoryId: string): ICategoryModel | undefined | null {
      return self?.categories.find(cat => cat && cat._id === categoryId);
    },
    getCategoriesFormatedForPath() {
      if(!self.categories) {return;}
      return (self.categories as ICategoryModel[]).slice().sort((c1, c2) => (c1.order > c2.order) ? 1 : -1).map(cat => {
        if(!cat) {return;}
        const specialTypeIn = ['RECOMMENDED_PATHS', 'ONGOING_PATHS'].includes(cat.type);
        const specialTypeOut = ['DAILY_EXERCISES'].includes(cat.type);
        if ((cat.inDashboard || specialTypeIn) && !specialTypeOut) {
          if(specialTypeIn) {
            const catItems = this.getSpecial(cat.type);

            if (!!catItems && catItems.length > 0) {
              return (
                {
                  data: catItems,
                  title: cat.title,
                  id: cat._id,
                });
            } else {
              return;
            }
          }
          return (
            {
              data: this.filterAccessibleItems(cat.categoryItem?.slice()),
              title: cat.title,
              id: cat._id,
            }
          );
        }
      }).filter(x => x);
    },
    get categoriesFormatedForChooseANewPath() {
      if(!self.categories) {return;}
      return (self.categories as ICategoryModel[]).slice().sort((c1, c2) => (c1.order > c2.order) ? 1 : -1).map(cat => {
        if(!cat) {return;}
        const specialTypeOut = [ 'ONGOING_PATHS', 'DAILY_EXERCISES'].includes(cat.type);
        const specialTypeIn = 'RECOMMENDED_PATHS' === cat.type;
        if ((cat.inDashboard || specialTypeIn) && !specialTypeOut) {
          if(specialTypeIn) {
            const catItems = this.getSpecial(cat.type);

            if (!!catItems && catItems.length > 0) {
              return (
                {
                  data: catItems,
                  title: cat.title,
                  id: cat._id,
                });
            } else {
              return;
            }
          }else {
            return (
              {
                data: this.filterAccessibleItems(cat.categoryItem?.slice()),
                title: cat.title,
                id: cat._id,
              }
            );
          }
        }
      }).filter(x => x);
    },
    getCategoriesFormatedForActivities() {
      if(!self.categories) {return;}
      return (self.categories as ICategoryModel[]).slice().sort((c1, c2) => (c1.order > c2.order) ? 1 : -1).map(cat => {
        if(!cat) {return;}
        const specialType = ['RECOMMENDED_PATHS', 'ONGOING_PATHS', 'DAILY_EXERCISES'].includes(cat.type);
        if (!cat.inDashboard && !specialType) {
          return (
            {
              data: this.filterAccessibleItems(cat.categoryItem?.map(x => ({...x, showActivityType: cat?.showCategoryItemsType}))?.slice()),
              title: cat.title,
              id: cat._id,
            }
          );
        }
      }).filter(x => x);
    },
    getCategoriesItemsFormatedForGenericPage(id: string) {
      const index = self.categories?.findIndex(cat => cat._id === id);
      if(index === -1) {return;}
      const cat = self.categories[index];
      let categoryItems = null;
      if (cat?.type !== 'REGULAR') {
        categoryItems = this.getSpecial(cat.type);
      } else {
        categoryItems = this.filterAccessibleItems(cat.categoryItem);

      }
      const items: any = [];
      if(!categoryItems || categoryItems.length === 0) {return;}
      categoryItems.forEach((pi: any, index: number) => {
        if (index % 2 == 0) {
          if (index === categoryItems.length - 1) {
            items.push({ path: true, count: 1, data: [pi] });
          } else {
            items.push({ path: true, count: 2, data: [pi, categoryItems[index + 1]] });
          }
        }
      });
      return items;
    },
    getColor(id: string) {
      if (self.categories) {
        const index = self.categories?.findIndex(cat => cat._id === id);
        if (index === -1) {
          return '#000000';
        } else {
          return self.categories[index].color;
        }
      } else {
        return '#000000';
      }
    },
    getCategoryItemFromContentId(id: string) {
      if(!self.categories) {return;}
      let catItemIndex = -1;
      const cat = self.categories.find(cat => {
        const catItem = cat?.categoryItem?.findIndex(ci => ci.contentId === id);
        if (!isUndefined(catItem) && catItem !== -1) {
          catItemIndex = catItem;
          return true;
        }
        return false;
      });
      return cat?.categoryItem?.[catItemIndex];
    },
    get recommendedCategory() {
      const index = self.categories?.findIndex(cat => cat && cat.type === 'RECOMMENDED_PATHS');
      if (!self.categories || index === -1) {
        return null;
      } else {
        return self.categories[index];
      }
    },
  })
  );

export type ICategoryStore = Instance<typeof CategoryStore>;
